import type { HTMLAttributes } from 'vue';
import { z } from 'zod';
import { Row, Column, ColumnDef, InitialTableState } from "@tanstack/vue-table";
import { i18n } from '@/i18n';
import { Models } from 'appwrite';
import { Component, FunctionalComponent } from "vue";
import { LucideProps, } from 'lucide-vue-next';

const transactionType = z.enum(["debit", "credit"]);

const transactionTypeTransformer = z.string().transform((val) => {
  if (val === i18n.t('createOp.form.rows.type.value.0').toLowerCase()) return "debit";
  if (val === i18n.t('createOp.form.rows.type.value.1').toLowerCase()) return "credit";
  return val;
}).pipe(transactionType);

export type Falsy = false | 0 | "" | null | undefined

export interface Category {
	value: string;
	color: string;
}

export interface BadgeContent {
	name: string,
	total: number,
}

export interface Categories {
	categoryId: string,
	percentage: number,
	amount: number,
	bank_account: BankAccount,
}

export interface CategoryDataType {
	amount: number,
	categoryId: string,
	percentage: number,
}

export interface CardType {
	name: Component,
	columns: number,
	cardTitle: string,
	cardDescription: string,
	props: object,
}

export type CardLineChartProps = {
	columns: number
	type?: 'debit' | 'credit'
	layout?: 'row' | 'col'
}

export type OperationStateChange = {
	type: 'added' | 'updated' | 'deleted',
	newOperation: Operation,
	oldOperation?: Operation,
	fieldsTouched?: Partial<SubmitFormOperation>,
}

export type CardLastTransactions<T> = {
	title: string
	tooltipContent: string
	fetcher: (params?: QueriesParamsGetOperations) => Promise<{operations: Array<T>}>;
	columns: ColumnDef<any, any>[]
	colSpan: number
	initialTableState?: InitialTableState
}

export type DataArrayItem = {
  $id?: string,
	name: string,
	icon: FunctionalComponent<LucideProps, any, any, any>
	routeName?: string,
	total?: number,
	onClick?: (value: boolean) => void;
}

export type DataArrayAccount = {
  $id: string,
	name: string,
	total: number | string
}

export type DataArrayProfile = {
  icon: FunctionalComponent<LucideProps, any, any, any>,
  name: string,
  routeName: string
}

export interface MenuItemsType {
	onClick?: (value: boolean) => void,
	icon: FunctionalComponent<object, any, any, any> | null,
	name: string,
	routeName?: string,
}

export interface OnboardingSteps {
  description: string,
  title: string,
}

export interface NewActions {
  icon: FunctionalComponent<LucideProps>,
  id: string,
  name: string,
  eventName: 'update:open' | 'new-transaction-clicked' | 'new-account-clicked',
  eventType: string
}

export interface Meta {
	page: number;
	pageSize: number;
	pageCount: number;
	total: number;
}

export interface BankAccount {
	$id: number | string,
	name: string,
}

export interface CardTicket {
	title: string;
	badgeList: BadgeContent[];
	ticks: Array<string>;
	tooltipContent: string,
	columns: number;
	showContentAmount?: boolean
	isFetching?: boolean;
	shouldDisplayFetching?: boolean
	layout?: 'col' | 'row'
	amount?: number | string;
	description?: string;
	titleClass?: HTMLAttributes['class'];
}

export type ResponsiveDrawerProps = {
	title: string;
	description: string;
	open: boolean;
	showCancelBtn?: boolean;
	buttonClass?: boolean,
};

export type Session = Pick<Models.Session, '$id' | 'clientName' | 'clientVersion' | 'clientType' | 'ip' | 'current' | 'countryName' | 'deviceModel' | 'osName' | 'osVersion' | 'deviceBrand'>;

export type TableOperationProps = {
	query: (params?: QueriesParamsGetOperations) => Promise<{operations: Array<Operation>, meta: {items_per_page: number, total: number, total_pages: number}}>
};

export type TableOperationCellProps = {
	row: Row<Operation>
};

export type TableOperationHeaderProps = {
	column: Column<Operation>
};

export type UpdateUserData = Models.User<Models.Preferences> & { mailing: Array<string> };

export type AlertDialogTableProps = {
	title: string;
	description: Array<string>;
	showWording?: boolean;
	onCancel?: () => void;
	onValid?: () => Promise<unknown>;
};

export const ZodGetCategories = z.object({
	id: z.string(),
}).optional()
export type GetCategories = z.infer<typeof ZodGetCategories>

export const ZodGetTotalDocumentsOps = z.object({
	is_demo: z.boolean(),
}).partial().optional()
export type GetTotalDocumentsOps = z.infer<typeof ZodGetTotalDocumentsOps>

export const ZodGetTotalDocuments = z.enum(['operations', 'categories', 'bankAccount']);
export type GetTotalDocuments = z.infer<typeof ZodGetTotalDocuments>

export const ZodModeGetAllAccounts = z.enum(['include', 'exclude'])
export type ModeGetAllAccounts = z.infer<typeof ZodModeGetAllAccounts>

export const ZodParamsGetAllAccounts = z.object({
  accountsId:  z.array(z.string().transform(el => el.toString())).optional(),
	is_demo: z.boolean().optional(),
})
export type ParamsGetAllAccounts = z.infer<typeof ZodParamsGetAllAccounts>;

export const ZodAggregateCategoriesSum = z.object({
	id: z.string(),
	datestart: z.string().datetime({offset: true}),
	dateend: z.string().datetime({offset: true}),
}).partial();
export type AggregateCategoriesSum = z.infer<typeof ZodAggregateCategoriesSum>

export const ZodAggregateAccountSum = z.object({
	datestart: z.string().datetime({offset: true}),
	dateend: z.string().datetime({offset: true}),
	id: z.string(),
	type: z.enum(['debit', 'credit']),
	is_demo: z.boolean(),
}).partial();
export type AggregateAccountSum = z.infer<typeof ZodAggregateAccountSum>;

export const ZodAggregationCategoriesSumResult = z.object({
	categories: z.object({categoryId: z.string(), total: z.number(), length: z.number()}).array(),
	totalDocuments: z.number(),
});
export type AggregationCategoriesSumResult = z.infer<typeof ZodAggregationCategoriesSumResult>

export const ZodQueriesParamsGetOperations = z.object({
  label: z.string(),
	limit: z.number().max(25),
	sort: z.string().regex(/^(asc|desc):(date|bank_account)(-(asc|desc):(date|bank_account))*$/),
	type: z.enum(['debit', 'credit'], { required_error: i18n.t('createOp.form.rows.type.error'), invalid_type_error: i18n.t('createOp.form.rows.type.errorType') }),
	bank_account_id: z.string(),
	categories: z.string(),
	datestart: z.string().datetime({ offset: true }),
	dateend: z.string().datetime({ offset: true }),
	amount: z.string().includes('-'),
	cursor: z.string(),
	cursordirection: z.enum(['before', 'next']),
	select: z.string(),
	page: z.number(),
	is_demo: z.boolean(),
}).partial();
export type QueriesParamsGetOperations = z.infer<typeof ZodQueriesParamsGetOperations>;

const isValidDate = (value: string) => !isNaN(Date.parse(value));

export const ZodAccount = z.object({
	$id: z.string(),
	userId: z.union([z.string(), z.undefined()]),
	$createdAt: z.string().refine(isValidDate),
	$updatedAt: z.string().refine(isValidDate),
	name: z.string().nonempty().max(25, i18n.t('modalAccount.form.name.errorLength')),
	total: z.union([z.number(), z.undefined(), z.literal("-")]),
	incomesTotal: z.union([z.number(), z.undefined(), z.literal("-")]),
	spendingTotal: z.union([z.number(), z.undefined(), z.literal("-")]),
	is_demo: z.boolean().optional(),
});
export type Account = z.infer<typeof ZodAccount>;

export const ZodSubmitFormOperation = z.object({
	label: z.string().max(25, i18n.t('createOp.form.rows.label.errorLength')).optional(),
	amount: z.number().positive(),
	categories: z.string().array().optional().default([]),
	type: transactionTypeTransformer,
	date: z.string().datetime({ offset: true }).optional().default(new Date().toISOString()),
	bank_account: z.string().nonempty().max(50),
	bank_account_id: z.number(),
	is_demo: z.boolean().default(false),
})
export type SubmitFormOperation = z.infer<typeof ZodSubmitFormOperation>;

export const ZodUpdateBankAccountName = ZodAccount.extend({
	bankAccountId: z.string(),
});
export type UpdateBankAccountName = z.infer<typeof ZodUpdateBankAccountName>;

export const ZodOperation = z.object({
	$id: z.string().nanoid(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
	date: z.string().datetime({ offset: true }),
	amount: z.number().positive(),
	bank_account: z.object({
		$id: z.string().nanoid(),
		name: z.string().nonempty().max(50),
		user_id: z.string().nanoid(),
	}),
	categories: z.string().array().optional().default([]),
	label: z.string().nonempty().max(25),
	type: z.enum(['debit', 'credit']),
	is_demo: z.boolean(),
});
export type Operation = z.infer<typeof ZodOperation>;

export const ZodFormOperationsProps = z.object({
	sheetFormType: z.enum(['add', 'edit']),
	rowIndex: z.number().nonnegative(),
	initialOperationValues: ZodSubmitFormOperation.or(z.object({})),
})
export type FormOperationProps = {
	sheetType: 'add' | 'edit';
	open: boolean;
	initialOperationValues?: Operation | undefined;
};


export interface BankAccount {
	$id: number | string,
	name: string,
	user: null | object,
}
